import React from "react";
import { useQuery } from "@tanstack/react-query";

import { Activity, AlertTriangle, Clock, Database, Refresh } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { fetchHealthStatus } from "@/lib/api";
import {
  blockedNote,
  componentSeverity,
  diagLogsRedisSeverity,
  energyHydrationSeverity,
  energyWriterSeverity,
  evaluateEnergyHydration,
  evaluateEnergyWriter,
  liveRedisSeverity,
  num,
  overallSeverity,
  writerAbsenceNote,
  type HealthDependency,
  type HealthIssue,
  type HealthStatus,
  type Severity,
} from "@/lib/health";
import { FORMAT_LOCALE, formatDuration } from "@voltie/localization-web";
import { useT, type Translate } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import { viewerInstant } from "@/app/displayFormat";

const REFRESH_MS = 12_000;

/* ------------------------------------------------------------------ */
/*  Formatting                                                         */
/* ------------------------------------------------------------------ */

/** "—" rather than 0 for absent numbers: a missing counter is not a zero one. */
function fmtNum(v: unknown, digits = 0): string {
  const n = num(v);
  return n === null ? "—" : n.toLocaleString(FORMAT_LOCALE, { maximumFractionDigits: digits });
}

function fmtBool(t: Translate, v: unknown): string {
  if (v === true || v === false) return t("operator.ops.yes_no", { value: v ? "yes" : "no" });
  return "—";
}

function fmtSeconds(v: unknown): string {
  const n = num(v);
  if (n === null) return "—";
  if (n < 60) return `${Math.round(n)}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
  if (n < 86400) return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
  return `${Math.floor(n / 86400)}d ${Math.floor((n % 86400) / 3600)}h`;
}

function fmtMs(v: unknown): string {
  const n = num(v);
  if (n === null) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n.toFixed(n < 10 ? 1 : 0)}ms`;
}

/** Time of day in the viewer's zone; a value the formatter refuses is shown as sent. */
function fmtClock(iso: string | null | undefined): string {
  return viewerInstant(iso, "timeSeconds") ?? "—";
}

/* ------------------------------------------------------------------ */
/*  Severity presentation                                              */
/* ------------------------------------------------------------------ */

const SEVERITY_VARIANT: Record<Severity, "success" | "warning" | "error" | "neutral"> = {
  ok: "success",
  warning: "warning",
  critical: "error",
  off: "neutral",
};

const DOT_CLASS: Record<Severity, string> = {
  ok: "bg-emerald-400",
  warning: "bg-amber-400",
  critical: "bg-red-500",
  off: "bg-muted-foreground/50",
};

function SeverityDot({ level }: { level: Severity }) {
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${DOT_CLASS[level]}`} />;
}

function SeverityBadge({ level }: { level: Severity }) {
  const t = useT();
  return <Badge variant={SEVERITY_VARIANT[level]}>{t("operator.ops.health.severity", { level })}</Badge>;
}

function IssueList({ issues }: { issues: HealthIssue[] }) {
  const t = useT();
  if (issues.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {issues.map((issue, i) => (
        <li
          key={i}
          className={`flex items-start gap-2 rounded-md border p-2 text-xs ${
            issue.level === "CRITICAL"
              ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400"
              : "border-amber-400/40 bg-amber-400/10 text-amber-600 dark:text-amber-400"
          }`}
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <span className="font-semibold">{t("operator.ops.health.issue_level", { level: issue.level === "CRITICAL" ? "critical" : "warning" })}</span> — {issue.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One label/value cell of a dense metric grid. */
function Metric({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: string; tone?: Severity }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[11px] uppercase tracking-wide opacity-55" title={label}>
        {label}
      </div>
      <div
        className={`font-mono text-sm ${
          tone === "critical"
            ? "text-red-600 dark:text-red-400"
            : tone === "warning"
              ? "text-amber-600 dark:text-amber-400"
              : ""
        }`}
      >
        {value}
      </div>
      {hint && <div className="truncate text-[11px] opacity-50" title={hint}>{hint}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component table                                                    */
/* ------------------------------------------------------------------ */

interface ComponentRow {
  key: string;
  label: string;
  level: Severity;
  detail: string;
  reason?: string | null;
}

function diagnosticsLogsDetail(t: Translate, d: HealthStatus["diagnostics_logs"] | undefined): string {
  // Older writers do not report their write state: their last push is their heartbeat.
  if (d?.write_error_active == null) return t("operator.ops.health.last_push_ago", { age: fmtSeconds(d?.last_push_age_s) });
  return [
    num(d.last_heartbeat_age_s) === null
      ? t("operator.ops.health.no_heartbeat")
      : t("operator.ops.health.heartbeat_ago", { age: fmtSeconds(d.last_heartbeat_age_s) }),
    num(d.last_write_ok_age_s) === null
      ? t("operator.ops.health.no_successful_write")
      : t("operator.ops.health.last_write_ago", { age: fmtSeconds(d.last_write_ok_age_s) }),
    num(d.write_failures) === null
      ? t("operator.ops.health.write_failures_unknown")
      : t("operator.ops.health.write_failures", { count: num(d.write_failures) ?? 0 }),
  ].join(" · ");
}

function buildComponentRows(t: Translate, s: HealthStatus): ComponentRow[] {
  const writerIssues = evaluateEnergyWriter(s.energy_writer, s.energy_hydration, t);
  const hydrationIssues = evaluateEnergyHydration(s.energy_hydration, s.energy_writer, t);
  const heartbeat = (age: unknown) =>
    num(age) === null ? t("operator.ops.health.no_heartbeat") : t("operator.ops.health.heartbeat_ago", { age: fmtSeconds(age) });
  const disabled = (c: { enabled?: boolean } | undefined) => c?.enabled === false;
  const disabledByConfig = t("operator.ops.health.disabled_by_config");
  // Not checked because the store it reports into is down: say so instead of a
  // heartbeat age that was never read.
  const unverified = (c: { blocked_by?: HealthDependency | null } | undefined) =>
    c?.blocked_by ? t("operator.ops.health.not_checked") : null;
  const blockedSubs = Object.entries(s.udp_listener?.blocked_components ?? {});
  const live = s.redis_live;
  const diagLogs = s.redis_diag_logs;

  const rows: ComponentRow[] = [
    {
      key: "core",
      label: t("operator.ops.health.component", { component: "core" }),
      level: componentSeverity(s.core),
      detail: t("operator.ops.health.core_detail", { pid: String(s.core?.pid ?? "—"), uptime: fmtSeconds(s.core?.uptime_s) }),
      reason: s.core?.reason,
    },
    {
      key: "redis",
      label: t("operator.ops.health.component", { component: "redis" }),
      level: componentSeverity(s.redis),
      detail: num(s.redis?.latency_ms) === null ? "—" : t("operator.ops.health.ping", { latency: fmtMs(s.redis?.latency_ms) }),
      reason: s.redis?.reason,
    },
    {
      key: "redis_live",
      label: t("operator.ops.health.component", { component: "redis_live" }),
      level: liveRedisSeverity(live),
      detail: !live
        ? t("operator.ops.health.live_not_reported")
        : live.mode === "shared"
          ? t("operator.ops.health.live_shared")
          : num(live.latency_ms) === null ? "—" : t("operator.ops.health.ping", { latency: fmtMs(live.latency_ms) }),
      reason: live?.ok === false
        ? t("operator.ops.health.live_down", { reason: live.reason || t("operator.ops.health.unreachable") })
        : live?.reason,
    },
    ...(diagLogs
      ? [{
          key: "redis_diag_logs",
          label: t("operator.ops.health.component", { component: "redis_diag_logs" }),
          level: diagLogsRedisSeverity(diagLogs),
          detail: diagLogs.config_error
            ? t("operator.ops.health.diag_logs_config_rejected")
            : num(diagLogs.latency_ms) === null ? "-" : t("operator.ops.health.ping", { latency: fmtMs(diagLogs.latency_ms) }),
          reason: diagLogs.ok === false
            ? t("operator.ops.health.diag_logs_down", { reason: diagLogs.reason || t("operator.ops.health.unreachable") })
            : diagLogs.reason,
        }]
      : []),
    {
      key: "postgres",
      label: t("operator.ops.health.component", { component: "postgres" }),
      level: componentSeverity(s.postgres),
      detail: num(s.postgres?.latency_ms) === null ? "—" : t("operator.ops.health.select_one", { latency: fmtMs(s.postgres?.latency_ms) }),
      reason: s.postgres?.reason,
    },
    {
      key: "udp_listener",
      label: t("operator.ops.health.component", { component: "udp_listener" }),
      level: componentSeverity(s.udp_listener),
      detail: [
        heartbeat(s.udp_listener?.last_heartbeat_age_s),
        s.udp_listener?.hostname ? t("operator.ops.health.on_host", { host: s.udp_listener.hostname }) : null,
        s.udp_listener?.missing_components?.length
          ? t("operator.ops.health.missing_components", { components: s.udp_listener.missing_components.join(", ") })
          : null,
        blockedSubs.length
          ? t("operator.ops.health.unchecked_components", {
              components: blockedSubs
                .map(([c, dep]) => t("operator.ops.health.unchecked_component", { component: c, dependency: dep }))
                .join(", "),
            })
          : null,
        s.udp_listener?.degraded_components?.length
          ? t("operator.ops.health.degraded_components", { components: s.udp_listener.degraded_components.join(", ") })
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      reason: s.udp_listener?.reason,
    },
    {
      key: "cmd_channel_listener",
      label: t("operator.ops.health.component", { component: "cmd_channel_listener" }),
      level: componentSeverity(s.cmd_channel_listener),
      detail: disabled(s.cmd_channel_listener)
        ? disabledByConfig
        : unverified(s.cmd_channel_listener) ?? heartbeat(s.cmd_channel_listener?.last_heartbeat_age_s),
      reason: blockedNote(s.cmd_channel_listener, t) ?? s.cmd_channel_listener?.reason,
    },
    {
      key: "diagnostics_logs",
      label: t("operator.ops.health.component", { component: "diagnostics_logs" }),
      level: componentSeverity(s.diagnostics_logs),
      detail: disabled(s.diagnostics_logs)
        ? disabledByConfig
        : unverified(s.diagnostics_logs) ?? diagnosticsLogsDetail(t, s.diagnostics_logs),
      reason: blockedNote(s.diagnostics_logs, t) ?? s.diagnostics_logs?.reason,
    },
    {
      key: "diagnostics_metrics",
      label: t("operator.ops.health.component", { component: "diagnostics_metrics" }),
      level: componentSeverity(s.diagnostics_metrics),
      detail: disabled(s.diagnostics_metrics) ? disabledByConfig : unverified(s.diagnostics_metrics) ?? [
        t("operator.ops.health.snapshot_ago", { age: fmtSeconds(s.diagnostics_metrics?.snapshot_age_s) }),
        num(s.diagnostics_metrics?.ttl_remaining_s) === null
          ? null
          : t("operator.ops.health.ttl", { ttl: fmtSeconds(s.diagnostics_metrics?.ttl_remaining_s) }),
      ]
        .filter(Boolean)
        .join(" · "),
      reason: blockedNote(s.diagnostics_metrics, t) ?? s.diagnostics_metrics?.reason,
    },
    {
      key: "energy_writer",
      label: t("operator.ops.health.component", { component: "energy_writer" }),
      level: energyWriterSeverity(s.energy_writer, s.energy_hydration),
      detail: unverified(s.energy_writer) ?? (
        s.energy_writer?.running === false
          ? s.energy_writer?.reason || t("operator.ops.health.not_running")
          : t("operator.ops.health.writer_detail", { backlog: fmtNum(s.energy_writer?.dirty_backlog), oldest: fmtMs(s.energy_writer?.oldest_dirty_age_ms) })),
      reason: writerIssues[0]?.message,
    },
    {
      key: "energy_hydration",
      label: t("operator.ops.health.component", { component: "energy_hydration" }),
      level: energyHydrationSeverity(s.energy_hydration, s.energy_writer),
      detail:
        s.energy_hydration?.completed === true
          ? t("operator.ops.health.hydration_detail", {
              history: disabled(s.energy_hydration) ? "yes" : "no",
              time: fmtClock(s.energy_hydration?.completed_at),
              rows: fmtNum(s.energy_hydration?.rows_hydrated),
            })
          : s.energy_hydration?.reason || t("operator.ops.health.not_completed"),
      reason: hydrationIssues[0]?.message ?? s.energy_hydration?.reason,
    },
  ];
  return rows;
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function SystemHealthPage() {
  const t = useT();
  const healthQ = useQuery({
    queryKey: ["health", "status"],
    queryFn: fetchHealthStatus,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });

  const data = healthQ.data;
  const overall = data ? overallSeverity(data) : "warning";
  const writer = data?.energy_writer;
  const hydration = data?.energy_hydration;
  const writerIssues = data ? evaluateEnergyWriter(writer, hydration, t) : [];
  const hydrationIssues = data ? evaluateEnergyHydration(hydration, writer, t) : [];
  const writerLevel = data ? energyWriterSeverity(writer, hydration) : "warning";
  const hydrationLevel = data ? energyHydrationSeverity(hydration, writer) : "warning";
  const absenceNote = data ? writerAbsenceNote(writer, hydration, t) : null;
  const rows = data ? buildComponentRows(t, data) : [];

  const oldestMs = num(writer?.oldest_dirty_age_ms);
  const rps = num(writer?.rows_per_second);
  const capacity = num(writer?.capacity_rows_per_second);
  const throughputTone: Severity | undefined =
    rps !== null && capacity !== null && capacity > 0 && rps < capacity * 0.5 ? "warning" : undefined;
  const oldestTone: Severity | undefined =
    oldestMs === null ? undefined : oldestMs > 300_000 ? "critical" : oldestMs > 120_000 ? "warning" : undefined;

  return (
    <div className="space-y-4 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">{t("operator.nav.system_health")}</h1>
            {data && <SeverityBadge level={overall} />}
          </div>
          <div className="text-sm opacity-70">
            {t("operator.ops.health.subtitle", { endpoint: "/health/status" })}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-xs opacity-60">
            <div className="flex items-center justify-end gap-1">
              <Clock className="h-3.5 w-3.5" />
              {healthQ.isFetching
                ? t("operator.ops.health.refreshing")
                : t("operator.ops.health.updated", { time: fmtClock(healthQ.dataUpdatedAt ? new Date(healthQ.dataUpdatedAt).toISOString() : null) })}
            </div>
            <div>{t("operator.ops.health.auto_every", { interval: formatDuration(REFRESH_MS, "milliseconds"), time: fmtClock(data?.checked_at) })}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => healthQ.refetch()} disabled={healthQ.isFetching}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>
      </div>

      {healthQ.isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t("operator.ops.health.load_failed", {
            endpoint: "/health/status",
            error: errorMessage(t, healthQ.error, { fallback: "operator.error.request_failed" }),
          })}
        </div>
      )}

      {!data && !healthQ.isError && <div className="text-sm opacity-60">{t("operator.ops.health.loading")}</div>}

      {data && (
        <>
          {/* --- Energy writer: the reason this page exists ------------- */}
          <Card>
            <CardContent className="space-y-4 py-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4" /> {t("operator.ops.health.component", { component: "energy_writer" })}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <SeverityBadge level={writerLevel} />
                  <span className="text-xs opacity-60">
                    {writer?.enabled === false
                      ? t("operator.ops.health.disabled_by_config")
                      : writer?.blocked_by
                        ? t("operator.ops.health.not_checked")
                        : writer?.running === false
                          ? t("operator.ops.health.not_running")
                          : t("operator.ops.health.heartbeat_ago", { age: fmtSeconds(writer?.heartbeat_age_s) })}
                  </span>
                </div>
              </div>

              <IssueList issues={writerIssues} />

              {/* Where the disabled-vs-stalled verdict came from: the backend's
                  config verdict, or (legacy payload) the hydration marker. */}
              {absenceNote && (
                <div className="rounded-md border border-border bg-muted/40 p-2 text-xs opacity-70">
                  {absenceNote}
                  {writer?.reason ? ` (${writer.reason})` : ""}
                </div>
              )}

              {/* Queue lag. Backlog size alone is expected to be large on a
                  100k fleet — oldest_dirty_age_ms is the real lag signal. */}
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">{t("operator.ops.health.group_queue")}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric
                    label={t("operator.ops.health.oldest_dirty_age")}
                    value={fmtMs(writer?.oldest_dirty_age_ms)}
                    tone={oldestTone}
                    hint={t("operator.ops.health.oldest_dirty_age_hint")}
                  />
                  <Metric
                    label={t("operator.ops.health.dirty_backlog")}
                    value={fmtNum(writer?.dirty_backlog)}
                    hint={t("operator.ops.health.dirty_backlog_hint")}
                  />
                  <Metric label={t("operator.ops.health.claimed_in_flight")} value={fmtNum(writer?.claimed_in_flight)} />
                  <Metric
                    label={t("operator.ops.health.projected_drain")}
                    value={fmtSeconds(writer?.projected_drain_seconds)}
                    hint={t("operator.ops.health.projected_drain_hint")}
                  />
                  <Metric
                    label={t("operator.ops.health.target_freshness")}
                    value={fmtSeconds(writer?.target_freshness_seconds)}
                    hint={
                      num(writer?.reference_fleet_units) === null
                        ? undefined
                        : t("operator.ops.health.for_units", { units: fmtNum(writer?.reference_fleet_units) })
                    }
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">{t("operator.ops.health.group_throughput")}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric label={t("operator.ops.health.rows_per_second")} value={fmtNum(writer?.rows_per_second, 1)} tone={throughputTone} />
                  <Metric
                    label={t("operator.ops.health.capacity_rows")}
                    value={fmtNum(writer?.capacity_rows_per_second, 1)}
                    hint={
                      num(writer?.interval_seconds) === null
                        ? undefined
                        : t("operator.ops.health.tick_every", { interval: fmtSeconds(writer?.interval_seconds) })
                    }
                  />
                  <Metric label={t("operator.ops.health.tick_duration")} value={fmtMs(writer?.tick_duration_ms)} />
                  <Metric label={t("operator.ops.health.tick_p99")} value={fmtMs(writer?.tick_ms_p99)} />
                  <Metric
                    label={t("operator.ops.health.tick_overran_interval")}
                    value={fmtBool(t, writer?.tick_overran_interval)}
                    tone={writer?.tick_overran_interval ? "warning" : undefined}
                  />
                  <Metric
                    label={t("operator.ops.health.ticks_overran_label")}
                    value={fmtNum(writer?.ticks_overran)}
                    tone={(num(writer?.ticks_overran) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label={t("operator.ops.health.ticks")} value={fmtNum(writer?.ticks)} />
                  <Metric
                    label={t("operator.ops.health.ticks_failed")}
                    value={fmtNum(writer?.ticks_failed)}
                    tone={(num(writer?.ticks_failed) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label={t("operator.ops.health.batch_p50_p99")} value={`${fmtMs(writer?.batch_ms_p50)} / ${fmtMs(writer?.batch_ms_p99)}`} />
                  <Metric label={t("operator.ops.health.batch_max")} value={fmtMs(writer?.batch_ms_max)} />
                </div>
              </div>

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">{t("operator.ops.health.group_rows")}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric label={t("operator.ops.health.rows_written")} value={fmtNum(writer?.rows_written)} />
                  <Metric
                    label={t("operator.ops.health.rows_rejected")}
                    value={fmtNum(writer?.rows_rejected)}
                    tone={(num(writer?.rows_rejected) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label={t("operator.ops.health.remarked_advanced")} value={fmtNum(writer?.remarked_advanced)} />
                  <Metric
                    label={t("operator.ops.health.released_on_failure")}
                    value={fmtNum(writer?.released_on_failure)}
                    tone={(num(writer?.released_on_failure) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric
                    label={t("operator.ops.health.missing_snapshots")}
                    value={fmtNum(writer?.missing_snapshots)}
                    tone={(num(writer?.missing_snapshots) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric
                    label={t("operator.ops.health.corrupt_snapshots")}
                    value={fmtNum(writer?.corrupt_snapshots)}
                    tone={(num(writer?.corrupt_snapshots) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label={t("operator.ops.health.reclaimed_leases")} value={fmtNum(writer?.reclaimed_expired_leases)} />
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">{t("operator.ops.health.group_redis")}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric label={t("operator.ops.health.running")} value={fmtBool(t, writer?.running)} />
                  <Metric label={t("operator.ops.health.redis_version")} value={writer?.redis_version || "—"} />
                  <Metric
                    label={t("operator.ops.health.supports_zadd_lt")}
                    value={fmtBool(t, writer?.redis_supports_zadd_lt)}
                    tone={writer?.redis_supports_zadd_lt === false ? "critical" : undefined}
                    hint={t("operator.ops.health.supports_zadd_lt_hint")}
                  />
                  <Metric label={t("operator.ops.health.version_checked")} value={fmtBool(t, writer?.redis_version_checked)} />
                  <Metric label={t("operator.ops.health.heartbeat_age")} value={fmtSeconds(writer?.heartbeat_age_s)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* --- Hydration -------------------------------------------- */}
          <Card>
            <CardContent className="space-y-3 py-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Database className="h-4 w-4" /> {t("operator.ops.health.hydration_title")}
                </CardTitle>
                <SeverityBadge level={hydrationLevel} />
              </div>

              <IssueList issues={hydrationIssues} />

              {hydration?.enabled === false && (
                <div className="rounded-md border border-border bg-muted/40 p-2 text-xs opacity-70">
                  {t("operator.ops.health.hydration_disabled_note")}
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
                <Metric label={t("operator.ops.health.completed")} value={fmtBool(t, hydration?.completed)} />
                <Metric label={t("operator.ops.health.started_at")} value={fmtClock(hydration?.started_at)} />
                <Metric label={t("operator.ops.health.completed_at")} value={fmtClock(hydration?.completed_at)} />
                <Metric label={t("operator.ops.health.rows_scanned")} value={fmtNum(hydration?.rows_scanned)} />
                <Metric label={t("operator.ops.health.rows_hydrated")} value={fmtNum(hydration?.rows_hydrated)} />
                <Metric label={t("operator.ops.health.rows_repaired")} value={fmtNum(hydration?.rows_repaired)} />
                <Metric label={t("operator.ops.health.rows_skipped_lower")} value={fmtNum(hydration?.rows_skipped_lower)} />
                {/* Only rendered when the deployment reports them: an absent
                    counter means "not reported", never zero. */}
                {num(hydration?.rows_failed) !== null && (
                  <Metric
                    label={t("operator.ops.health.rows_failed_label")}
                    value={fmtNum(hydration?.rows_failed)}
                    tone={(num(hydration?.rows_failed) ?? 0) > 0 ? "critical" : undefined}
                  />
                )}
                {num(hydration?.redis_round_trips) !== null && (
                  <Metric label={t("operator.ops.health.redis_round_trips")} value={fmtNum(hydration?.redis_round_trips)} />
                )}
              </div>

              {hydration?.reason && !hydration?.completed && (
                <div className="text-xs opacity-60">{hydration.reason}</div>
              )}
            </CardContent>
          </Card>

          {/* --- All components --------------------------------------- */}
          <Card>
            <CardContent className="py-5">
              <CardTitle className="mb-3 text-sm">{t("operator.ops.health.components")}</CardTitle>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide opacity-55">
                      <th className="py-2 pr-3 font-medium">{t("operator.ops.health.col_component")}</th>
                      <th className="py-2 pr-3 font-medium">{t("operator.ops.health.col_status")}</th>
                      <th className="py-2 font-medium">{t("operator.ops.health.col_detail")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key} className="border-b border-border/60 last:border-0 align-top">
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <SeverityDot level={row.level} />
                            <span>{row.label}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <SeverityBadge level={row.level} />
                        </td>
                        <td className="py-2 font-mono text-xs opacity-75">
                          <div>{row.detail || "—"}</div>
                          {row.reason && row.level !== "ok" && (
                            <div className="mt-0.5 font-sans opacity-70">{row.reason}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
